import React, { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

// Client deck: hook order — highlight case studies early; no Proof, Who we serve (price), or Team
const SLIDES = [
  { id: 'title', label: 'Title' },
  { id: 'case-study-highlight', label: 'Results' },
  { id: 'problem', label: 'Problem' },
  { id: 'solution', label: 'Solution' },
  { id: 'value', label: 'Value' },
  { id: 'case-study-value', label: 'Case 1' },
  { id: 'case-study-impact', label: 'Case 2' },
  { id: 'competitive', label: 'Why Us' },
  { id: 'deployment-timeline', label: 'Timeline' },
  { id: 'demo-videos', label: 'Demo' },
  { id: 'closing', label: 'Close' },
];

export function ClientDeckNav() {
  const [active, setActive] = useState('title');

  useEffect(() => {
    let observer = null;
    const t = setTimeout(() => {
      observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          if (visible.length > 0 && visible[0].target.id) {
            setActive(visible[0].target.id);
          }
        },
        { threshold: 0.3, root: null }
      );
      SLIDES.forEach((s) => {
        const el = document.getElementById(s.id);
        if (el) observer.observe(el);
      });
    }, 100);
    return () => {
      clearTimeout(t);
      if (observer) observer.disconnect();
    };
  }, []);

  const currentIndex = SLIDES.findIndex((s) => s.id === active);

  const scrollToTop = () => {
    const el = document.getElementById(SLIDES[0].id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top, left: 0, behavior: 'auto' });
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  const scrollToBottom = () => {
    const el = document.getElementById(SLIDES[SLIDES.length - 1].id);
    if (el) {
      const top = el.getBoundingClientRect().bottom - window.innerHeight + window.scrollY;
      window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'auto' });
    } else {
      const top = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      window.scrollTo({ top, behavior: 'auto' });
    }
  };

  const scrollTo = (id) => {
    if (SLIDES[0].id === id) return scrollToTop();
    if (SLIDES[SLIDES.length - 1].id === id) return scrollToBottom();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goPrev = () => {
    if (currentIndex <= 0) return;
    const targetIndex = currentIndex - 1;
    if (targetIndex === 0) scrollToTop();
    else if (targetIndex === SLIDES.length - 1) scrollToBottom();
    else scrollTo(SLIDES[targetIndex].id);
  };

  const goNext = () => {
    if (currentIndex >= SLIDES.length - 1) return;
    const targetIndex = currentIndex + 1;
    if (targetIndex === 0) scrollToTop();
    else if (targetIndex === SLIDES.length - 1) scrollToBottom();
    else scrollTo(SLIDES[targetIndex].id);
  };

  return (
    <nav
      className="fixed right-2 top-1/2 z-[100] flex -translate-y-1/2 flex-col items-center gap-1 w-10 sm:right-4"
      aria-label="Slide navigation"
    >
      <button
        type="button"
        onClick={goPrev}
        disabled={currentIndex === 0}
        className="mb-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/90 text-gray-500 shadow-sm backdrop-blur-sm transition-colors hover:bg-white hover:text-gray-900 disabled:opacity-30"
        aria-label="Previous slide"
      >
        <ChevronUp className="h-4 w-4" />
      </button>
      <div className="flex flex-col items-center gap-1.5">
        {SLIDES.map((s) => (
          <div key={s.id} className="group relative flex items-center justify-center">
            <span
              className="pointer-events-none absolute right-full mr-2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-900 shadow-md opacity-0 transition-opacity duration-150 group-hover:opacity-100"
              style={{ top: '50%', transform: 'translateY(-50%)' }}
            >
              {s.label}
            </span>
            <button
              type="button"
              onClick={() => scrollTo(s.id)}
              className="flex h-8 w-8 shrink-0 items-center justify-center"
              aria-label={`Go to ${s.label}`}
              aria-current={active === s.id ? 'true' : undefined}
            >
              <span
                className={`block h-2.5 w-2.5 rounded-full border-2 transition-all ${
                  active === s.id
                    ? 'scale-110 border-primary-500 bg-primary-500'
                    : 'border-gray-200 bg-white hover:border-primary-400 hover:bg-primary-100'
                }`}
              />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={goNext}
        disabled={currentIndex === SLIDES.length - 1}
        className="mt-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/90 text-gray-500 shadow-sm backdrop-blur-sm transition-colors hover:bg-white hover:text-gray-900 disabled:opacity-30"
        aria-label="Next slide"
      >
        <ChevronDown className="h-4 w-4" />
      </button>
    </nav>
  );
}
